import { useAsync } from 'react-use';
import { CircularProgress, FormHelperText, TextField } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import { Squad360ServiceApiRef, SimpleApp } from '../../services';
import { useApi } from '@backstage/core-plugin-api';
import { FC } from 'react';
import { PersistentAutocomplete, clearStoredValue } from '../PersistentAutocomplete';

export type CostCenterOnChangeValue = {
  costCenter: string;
};

// localStorage key for storing the last used cost center
const LAST_COST_CENTER_STORAGE_KEY = 'cost-center-last-used';

/**
 * Utility function to clear the last used cost center from localStorage
 * This is exported for use in tests and external components
 */
export const clearLastUsedCostCenter = (): void => {
  clearStoredValue(LAST_COST_CENTER_STORAGE_KEY);
};

interface CostCenterProps {
  onChange(value: CostCenterOnChangeValue): void;
  onBlur?: () => void;
  initialValue?: CostCenterOnChangeValue;
  value?: CostCenterOnChangeValue;
}

export const CostCenter: FC<CostCenterProps> = ({ onChange, onBlur, initialValue, value }) => {
  const squad360 = useApi(Squad360ServiceApiRef);

  const archerOptions = useAsync(async () => squad360.getAllApplicationsSimple(), [squad360]);

  let archerOptionsWithCostCenters: SimpleApp[] = [];
  if (!!archerOptions.value) {
    const costCenters = archerOptions.value.map(option => option.costCenter);
    archerOptionsWithCostCenters = archerOptions.value
      .filter((option, index) => {
        return option.costCenter !== null && costCenters.indexOf(option.costCenter) === index;
      })
      .sort((x, y) => (x.costCenter < y.costCenter ? -1 : 1));
  }

  // Find initial option based on initialValue prop
  const initialOption = initialValue
    ? archerOptionsWithCostCenters.find(
        app =>
          app.costCenter === initialValue.costCenter || app.costCenter === initialValue.costCenter.replace('/', '-'),
      ) || null
    : null;

  // Controlled value - find the option that matches the value prop (only when controlled)
  const controlledOption = value
    ? archerOptionsWithCostCenters.find(
        app => app.costCenter === value.costCenter || app.costCenter === value.costCenter.replace('/', '-'),
      ) || null
    : undefined;

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <PersistentAutocomplete
        storageKey={LAST_COST_CENTER_STORAGE_KEY}
        disabled={archerOptions.error !== undefined}
        id="costCenter"
        data-testid="costCenter"
        disableClearable
        getOptionLabel={(option: SimpleApp) => (option.costCenter !== null ? `${option.costCenter}` : '')}
        options={archerOptionsWithCostCenters}
        defaultValue={initialOption}
        value={value ? controlledOption : undefined}
        onChange={(application: SimpleApp | null) => {
          if (application) {
            const out = {
              costCenter: application.costCenter!.replace('-', '/'),
            };
            onChange(out);
          }
        }}
        onBlur={onBlur}
        style={{ width: 300, display: 'inline-flex' }}
        renderInput={params => <TextField required {...params} label="Cost Center" variant="outlined" />}
      />

      {archerOptions.loading && <CircularProgress style={{ marginLeft: 10 }} />}
      {!!archerOptions.error && (
        <>
          <ErrorIcon style={{ marginLeft: 10, marginRight: 10, color: 'red' }} />
          <FormHelperText>There was an issue getting the Cost center </FormHelperText>
        </>
      )}
    </div>
  );
};
